import { useState } from "react";
import { ListGroup, Form, Button, Spinner } from "react-bootstrap";
import Loader from "./Loader";
import Message from "./Message";
import Rating from "./Rating";
import { toast } from "react-toastify";
import {
  useCreateOrderedProductReviewMutation,
  useGetOrderedProductReviewsByIdQuery
} from "../slices/ordersApiSlice";
import { dateFormatter } from "../utils/dateHelper";

const OrderProductReview = ({ order, orderId, refetch }) => {
  const [productReviews, setProductReviews] = useState({});
  const [savingOrderedProductReview, setSavingOrderedProductReview] =
    useState(false);

  const {
    data: orderedProductReviews = [],
    isLoading: isLoadingOrderedProductReviews,
    refetch: refetchGetOrderedProductReviews,
  } = useGetOrderedProductReviewsByIdQuery(orderId);

  const [createOrderedProductReview, { isLoading: isLoadingProductReview }] =
    useCreateOrderedProductReviewMutation();

  const handleProductReviewSubmit = async (e, productId) => {
    e.preventDefault();
    try {
      setSavingOrderedProductReview(true);
      const { rating, comment } = productReviews[productId];

      await createOrderedProductReview({
        orderId,
        productId,
        rating,
        comment,
      }).unwrap();

      toast.success("Ordered Product review submitted", {autoClose: 1000});

      setProductReviews({
        ...productReviews,
        [productId]: { rating: 0, comment: "" },
      });

      refetch();
      refetchGetOrderedProductReviews();
    } catch (err) {
      toast.error(err?.data?.message || err.error, {autoClose: 2000});
    } finally {
      setSavingOrderedProductReview(false);
    }
  };

  return (
    <ListGroup.Item>
      <h2>Product Reviews</h2>

      {isLoadingOrderedProductReviews ? (
        <Loader />
      ) : (
        order.orderItems.map((item) => {
          const review = orderedProductReviews.find(
            (r) => r.product?.toString() === item.product?.toString()
          );

          return (
            <div key={item._id} className="mb-4 white-section">
              {review ? (
                <>
                  <h5>
                    Review for <strong>{item.name}</strong>
                  </h5>
                  <Rating
                    value={review.rating}
                    text={`reviewed on ${dateFormatter(review.createdAt)}`}
                  />
                  <p>{review.comment}</p>
                </>
              ) : !order.delivery.isDelivered ? (
                <>
                  <h5>
                    Review for <strong>{item.name}</strong>
                  </h5>
                  <Message variant="info">
                    Review can be provided after order delivery
                  </Message>
                </>
              ) : Date.now() >
                new Date(order.delivery.deliveredAt).getTime() +
                  14 * 24 * 60 * 60 * 1000 ? (
                <>
                  <h5>
                    Review for <strong>{item.name}</strong>
                  </h5>
                  <Message variant="info">
                    Review window expired (14 days past order delivery)
                  </Message>
                </>
              ) : (
                <>
                  <h5>
                    Write Product Review for <strong>{item.name}</strong>
                  </h5>

                  {isLoadingProductReview && <Loader />}

                  <Form
                    onSubmit={(e) =>
                      handleProductReviewSubmit(e, item.product)
                    }
                  >
                    <Form.Group
                      controlId={`rating-${item.product}`}
                      className="my-2"
                    >
                      <Form.Label>Rating</Form.Label>
                      <Form.Control
                        as="select"
                        value={productReviews[item.product]?.rating || ""}
                        onChange={(e) =>
                          setProductReviews({
                            ...productReviews,
                            [item.product]: {
                              ...productReviews[item.product],
                              rating: Number(e.target.value),
                            },
                          })
                        }
                      >
                        <option value="">Select rating</option>
                        <option value="1">1 - Poor</option>
                        <option value="2">2 - Fair</option>
                        <option value="3">3 - Good</option>
                        <option value="4">4 - Very Good</option>
                        <option value="5">5 - Excellent</option>
                      </Form.Control>
                    </Form.Group>

                    <Form.Group
                      controlId={`comment-${item.product}`}
                      className="my-2"
                    >
                      <Form.Label>Comment</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows="3"
                        value={productReviews[item.product]?.comment || ""}
                        onChange={(e) =>
                          setProductReviews({
                            ...productReviews,
                            [item.product]: {
                              ...productReviews[item.product],
                              comment: e.target.value,
                            },
                          })
                        }
                      />
                    </Form.Group>

                    <Button
                      disabled={
                        isLoadingProductReview || savingOrderedProductReview
                      }
                      type="submit"
                      variant="primary"
                    >
                      {savingOrderedProductReview ? (
                        <>
                          <Spinner animation="border" size="sm" /> Submitting...
                        </>
                      ) : (
                        `Submit Review for ${item.name}`
                      )}
                    </Button>
                  </Form>
                </>
              )}
            </div>
          );
        })
      )}
    </ListGroup.Item>
  );
};

export default OrderProductReview;